import React, { FC, MouseEvent, ReactNode } from "react";
import {
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant,
} from "enums/Button";
import { CustomButton, Spinner, WithIcons } from "./Button.style";

type UniversalClickHandler =
  | ((event: MouseEvent<HTMLButtonElement>) => void)
  | ((...args: any[]) => void);
export interface ButtonProps {
  children: ReactNode;
  isLoading?: boolean;
  disabled?: boolean;
  type?: ButtonType;
  size?: ButtonSize;
  variant?: ButtonVariant;
  color?: ButtonColor;
  // Stretches the button across its container.
  fullWidth?: boolean;
  // An icon in front of the label; the spinner takes its place while loading.
  leading?: ReactNode;
  // An icon after the label, such as the chevron of a button that moves on to
  // a next step. It stays while loading.
  trailing?: ReactNode;
  title?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  tabIndex?: number;
  hidden?: boolean;
  className?: string;
}

const Button: FC<ButtonProps> = ({
  children,
  isLoading = false,
  color = ButtonColor.Muted,
  variant = ButtonVariant.Basic,
  size = ButtonSize.Small,
  fullWidth = false,
  leading,
  trailing,
  tabIndex = 0,
  disabled = false,
  type = ButtonType.Button,
  className = "",
  ...otherProps
}) => {
  /* Bare text children get their own element so the spinner is inserted
     before a node React owns. Browser page translation (Chrome, Yandex,
     Safari) replaces text nodes with its own wrappers, and inserting the
     spinner before such a stale text node throws NotFoundError. */
  const label = React.Children.map(children, (child) =>
    typeof child === "string" || typeof child === "number" ? (
      <span>{child}</span>
    ) : (
      child
    ),
  );
  const showLeading = !!leading && !isLoading;

  return (
    <CustomButton
      $size={size}
      $color={color}
      $variant={variant}
      $fullWidth={fullWidth}
      tabIndex={tabIndex}
      disabled={disabled}
      type={type}
      className={className}
      {...otherProps}
    >
      {isLoading && <Spinner isActive={isLoading} />}
      {showLeading || trailing ? (
        <WithIcons>
          {showLeading && leading}
          <span>{label}</span>
          {trailing}
        </WithIcons>
      ) : (
        label
      )}
    </CustomButton>
  );
};

export default React.memo(Button);
